import { v4 as uuid } from "uuid";
import { findCreditScope } from "./creditScopes";
import { isDuplicateBy } from "./custom";
import { FrameworkTargetCategory, frameworksList } from "./list";
import { categoryByTargetType, isOtherEnvironmentalSubCategory, subCategoryByCategory } from "../../entities/environmentalCommitment";
import { EnvironmentalTargetMeasure } from "../../entities/environmentalTarget";
import { PolicyCategory, PolicyFrequency, PolicyType, RegisterSubject } from "../../entities/projectPolicy";
import { calculateTargetValueRealised } from "../../utils/calc";
import { getEmissionFactorTypeForEnvironmentalCommitment, getMetricTypeForEnvironmentalSelection } from "../../utils/environmentalProcessor";
import { deriveEnvironmentalTargetMeasuredBy } from "../../utils/environmentalTarget";
import { getCountryByCode, getStateByCodeAndCountry } from "../location";
import { SocialCommitmentTypeHour } from "../packageLabourHourCommitment";
import { ProjectCommmitmentGenerationSource } from "../project/enums";
import { LabourHourFactorCountingMethod, ProjectLabourHourCommitmentType } from "../projectLabourHourCommitment";
import { ProjectSocialSpendCommitmentType } from "../projectSocialSpendCommitment/enums";
export { FrameworkTargetCategory } from "./list";
export { CUSTOM_FRAMEWORK_ID_PREFIX, isCustomFrameworkId, isDuplicateFrameworkName, suggestFrameworkAbbreviation } from "./custom";
export { FrameworkId, frameworkIds } from "./frameworkId";
export { compareCreditCodes, coverageKindTakesSeveral, coverageTargetId, creditCoverageSourceForLevel, creditCoverageThresholdsAt, creditPointsAtLevel, creditPointsForAchievement, creditTargetIdsAtLevel, creditTargetValueForLevel, creditTrackFor, creditsInCodeOrder, findCredit, findCreditScope, frameworkCreditScopes, isRatingToolFramework, unmeasuredLowerLevelIds, unmeasuredThresholdsAt } from "./creditScopes";
export { allowsNotApplicable, categoryPointsFromCredits, declarableCreditsInCategory, declarableCreditsInCodeOrder, findDeclarableScope, frameworkDeclarableScopes, isCreditDeclarable, isCreditTracked, isMinimumExpectationDeclarable, minimumExpectationCredits, parentCreditCode, trackedPointsInCategory } from "./declaredCredits";
export { CreditState, creditStateFor, RequirementState } from "./creditStatus";
export { coveragePercent, coverageRequirementState, coverageRowsNeeded, meetsCoverage } from "./coverage";
/**
 * Requirements more than one scheme asks for, by `sharedRequirementKey`.
 *
 * A reference list, not a behaviour: nothing is deduped today. See the note on
 * `FrameworkTargetBase.sharedRequirementKey` for why.
 */
export const sharedRequirementNames = {
    "contracts-targets-written-in": "Contracts showing the targets written in",
    "iso20400-procurement-strategy": "Sustainable / responsible procurement strategy (ISO 20400)",
    "rap-actions-log": "RAP Actions Log",
    "rap-endorsement": "RAP + Reconciliation Australia endorsement",
    "sustainability-induction-register": "Sustainability Induction Register",
    "training-attendance-register": "Training Attendance Register",
    "waste-management-plan": "Waste Management Plan"
};
export const frameworkTargetCategoryToString = (targetCategory) => {
    switch (targetCategory) {
        case FrameworkTargetCategory.Hours:
            return "Hours";
        case FrameworkTargetCategory.Spend:
            return "Spend";
        case FrameworkTargetCategory.LocalContent:
            return "Local Content";
        case FrameworkTargetCategory.Environmental:
            return "Environmental";
        case FrameworkTargetCategory.Documents:
            return "Documents";
    }
};
export const createFrameworkKey = ({ country, frameworkId, state }) => `${country}:${state || ""}:${frameworkId}`;
export const frameworksByKey = new Map(frameworksList.map((framework) => [createFrameworkKey(framework), framework]));
export const frameworksByTargetCategory = new Map(Object.values(FrameworkTargetCategory).map((targetCategory) => [
    targetCategory,
    frameworksList.filter((framework) => framework.targets.some((target) => target.targetCategory === targetCategory))
]));
export const getFrameworkDisplayLocation = (framework) => {
    const countryFullName = getCountryByCode(framework.country)?.name ?? framework.country;
    const stateFullName = framework.state
        ? (getStateByCodeAndCountry(framework.state, framework.country)?.name ??
            framework.state)
        : undefined;
    return stateFullName
        ? `${stateFullName}, ${countryFullName} (State)`
        : `${countryFullName} (National)`;
};
export const findFramework = (frameworkId, country, state) => frameworksByKey.get(createFrameworkKey({ country, frameworkId, state }));
/**
 * How a project's framework is NAMED and ABBREVIATED. Catalogue first, then the
 * row's own — a built-in stores no abbreviation, a custom policy has the only
 * copy. One pair of resolvers because the sidebar, the source chip, the link
 * field and the picker all name a framework this way.
 */
// `||`, not `??`, and deliberately unlike `frameworkAbbreviationFor` below: an
// empty-string name is not a name anyone authored, so it must fall through the
// same as `undefined` would. An empty abbreviation is still a real answer (a
// blank monogram renders fine), which is why that one can afford `??`.
export const frameworkNameFor = (framework) => findFramework(framework.frameworkId, framework.country, framework.state)
    ?.frameworkName ||
    framework.frameworkName ||
    framework.frameworkId;
/**
 * `frameworkId` and `country` are OPTIONAL here, unlike on
 * `ProjectFrameworkLineItem` itself: a framework TEMPLATE has no catalogue
 * identity to look up (it is org-authored, never a catalogue entry), and
 * `isDuplicateFrameworkAbbreviation` runs over both. With neither to key a
 * lookup on, this falls straight to the row's own abbreviation.
 */
export const frameworkAbbreviationFor = (framework) => (framework.frameworkId && framework.country
    ? findFramework(framework.frameworkId, framework.country, framework.state)
        ?.frameworkAbbreviation
    : undefined) ?? framework.frameworkAbbreviation;
/**
 * The abbreviation WITH the adopted version — "RAP-v1".
 *
 * Separate from {@link frameworkAbbreviationFor}, and built on it so the two
 * cannot drift, because that one feeds the collapsed sidebar's monogram rail
 * and `isDuplicateFrameworkAbbreviation`. A monogram is a few characters wide;
 * "RAP-v1" does not belong there. The commitment source chip is where the
 * version answers a real question — which version of the policy put this
 * target here — so the chip reads this and nothing else does.
 *
 * Unversioned rows (every catalogue framework, every hand-written policy) get
 * the bare abbreviation back, unchanged.
 */
export const frameworkVersionedAbbreviationFor = (framework) => {
    const abbreviation = frameworkAbbreviationFor(framework);
    // A NUMBER, not merely "not undefined". A row that carries no version comes
    // back from the database as NULL, and `!== undefined` let that through to
    // compose "RAP-vnull" — which is what shipped the first time. Every row
    // written before this column existed is in exactly that state, so the guard
    // has to hold for them too, not only for a clean save.
    if (!abbreviation || !Number.isFinite(framework.frameworkVersion)) {
        return abbreviation;
    }
    return `${abbreviation}-v${framework.frameworkVersion}`;
};
/**
 * The collapsed sidebar is a column of monograms, so two policies reading "LBP"
 * are indistinguishable. Checked against built-ins too — they share one rail —
 * and case-insensitively, since both draw the same monogram.
 */
export const isDuplicateFrameworkAbbreviation = (abbreviation, frameworks, excludeId) => {
    if (!abbreviation.trim()) {
        return false;
    }
    return isDuplicateBy(abbreviation, frameworks, frameworkAbbreviationFor, excludeId);
};
export const createProjectFramework = (projectId, framework) => {
    // A rating tool starts at its default rating and pursues no credit.
    const scope = findCreditScope(framework.frameworkId);
    return {
        country: framework.country,
        created: new Date(),
        frameworkId: framework.frameworkId,
        frameworkName: framework.frameworkName,
        id: uuid(),
        projectId,
        state: framework.state,
        ...(scope ? { credits: [], targetedRating: scope.defaultRatingId } : {})
    };
};
/**
 * The categories a framework can actually create something from.
 *
 * Declared beside the `create*FromFramework` generators, because that is what
 * makes it true: a category with no generator produces nothing on every project
 * that adopts the framework, silently.
 *
 * Environmental is here because it now has both generators, and it creates two
 * things — a target AND the category leaves that target measures. Adopting a
 * framework therefore DOES switch Environmental reporting on, which is on
 * exactly when the project holds a commitment. Deliberate: a target over
 * categories the project does not report is one nobody can answer, and set-up
 * says what will be created before it happens.
 *
 * LocalContent is deliberately absent: its targets are declared so the Local
 * Content step can name the policy asking for them, and the categories are the
 * user's to enter.
 */
export const TRACKED_FRAMEWORK_TARGET_CATEGORIES = [
    FrameworkTargetCategory.Hours,
    FrameworkTargetCategory.Spend,
    FrameworkTargetCategory.Documents,
    FrameworkTargetCategory.Environmental
];
export const isTrackedFrameworkTargetCategory = (targetCategory) => TRACKED_FRAMEWORK_TARGET_CATEGORIES.includes(targetCategory);
/**
 * One entry that generation will actually act on: a tracked category, minus the
 * entries opted out of it.
 *
 * The single definition, shared by the generators and by the cards, so a
 * requirement cannot be listed as trackable on screen while producing nothing.
 */
export const isGeneratableFrameworkTarget = (target) => target.generatable !== false &&
    isTrackedFrameworkTargetCategory(target.targetCategory);
/**
 * An entry with no `targetValue` is report-only, so it becomes the matching
 * tracking-only type. Seeding 0 into the stated type would read as met.
 */
const generatedHoursMetricType = (target) => {
    if (target.targetValue !== undefined) {
        return target.targetMetricType;
    }
    return target.targetMetricType ===
        ProjectLabourHourCommitmentType.Headcount ||
        target.targetMetricType === ProjectLabourHourCommitmentType.HeadcountRatio
        ? ProjectLabourHourCommitmentType.TotalHeadcount
        : ProjectLabourHourCommitmentType.TotalHours;
};
export const createHoursTargetsFromFramework = (framework) => {
    const definition = findFramework(framework.frameworkId, framework.country, framework.state);
    if (!definition) {
        return [];
    }
    return definition.targets
        .filter((target) => target.targetCategory === FrameworkTargetCategory.Hours)
        .map((target) => ({
        colour: "#000000",
        created: new Date(),
        employmentLevel: target.employmentLevels ?? [],
        factorCountingMethod: target.factorCountingMethod ??
            LabourHourFactorCountingMethod.AllFactorsCombined,
        gender: target.gender ?? [],
        generationSource: ProjectCommmitmentGenerationSource.Framework,
        id: uuid(),
        includeArchivedEmployees: true,
        newEntrantToConstruction: target.newEntrantToConstruction,
        occupationType: target.occupationType ?? [],
        projectId: framework.projectId,
        socialFactors: target.socialFactors ?? [],
        sourceFrameworkId: framework.frameworkId,
        sourceFrameworkTargetId: target.id,
        targetDenominator: target.targetDenominator,
        targetDescription: target.targetName,
        targetMetricType: generatedHoursMetricType(target),
        targetName: SocialCommitmentTypeHour.DefaultHour,
        // A tracking-only target stores 0, as the target form saves one.
        targetValue: target.targetValue ?? 0,
        targetValueRealised: 0
    }));
};
export const createSpendTargetsFromFramework = (framework, options) => {
    const definition = findFramework(framework.frameworkId, framework.country, framework.state);
    if (!definition) {
        return [];
    }
    return definition.targets
        .filter((target) => target.targetCategory === FrameworkTargetCategory.Spend)
        .map((target) => ({
        certifications: target.certifications ?? [],
        colour: "#000000",
        created: new Date(),
        entityTypes: target.entityTypes ?? [],
        generationSource: ProjectCommmitmentGenerationSource.Framework,
        id: uuid(),
        projectId: framework.projectId,
        sourceFrameworkId: framework.frameworkId,
        sourceFrameworkTargetId: target.id,
        targetDescription: target.targetName,
        // Frameworks define invoiced Social Spend targets; employee cost is a
        // tenant-configured addition, never seeded from a Framework definition.
        // No `targetValue` is report-only; see `generatedHoursMetricType`.
        targetMetricType: target.targetValue === undefined
            ? ProjectSocialSpendCommitmentType.InvoiceTotal
            : ProjectSocialSpendCommitmentType.Invoice,
        targetName: target.commitmentType,
        // A tracking-only target stores 0 for both figures, as the form saves one.
        targetValue: target.targetValue ?? 0,
        targetValueRealised: calculateTargetValueRealised(target.targetValue ?? 0, options?.contractSpendBase ?? 0)
    }));
};
/**
 * The category leaves a set of environmental requirements needs, given nothing
 * but their scopes.
 *
 * Extracted so the CATALOGUE path and an organisation's own framework share it.
 * They create the same thing from the same rule — which leaves a scope brings,
 * how a bare category fans out, how duplicates across entries collapse — and
 * two copies of that rule is two rules that can disagree. `frameworkStaging`
 * says the same thing about the module gates, for the same reason.
 */
export const environmentalCommitmentsForScopes = (scopes, owner) => {
    const recordsByKey = new Map();
    scopes.forEach((target) => {
        const categories = target.category
            ? [target.category]
            : (categoryByTargetType[target.targetType] ?? []);
        categories.forEach((category) => {
            const subCategories = (subCategoryByCategory[category] ?? [])
                .filter((subCategory) => !isOtherEnvironmentalSubCategory(subCategory))
                .filter((subCategory) => !target.subCategory || subCategory === target.subCategory);
            subCategories.forEach((subCategory) => {
                const key = `${target.targetType}:${category}:${subCategory}`;
                if (recordsByKey.has(key)) {
                    return;
                }
                const derivedEmissionFactorType = getEmissionFactorTypeForEnvironmentalCommitment({
                    category,
                    sub_category: subCategory,
                    target_type: target.targetType
                });
                recordsByKey.set(key, {
                    category,
                    created: new Date(),
                    ...(derivedEmissionFactorType
                        ? { emission_factor_type: derivedEmissionFactorType }
                        : {}),
                    generation_source: ProjectCommmitmentGenerationSource.Framework,
                    metric_type: getMetricTypeForEnvironmentalSelection({
                        category,
                        subCategory
                    }),
                    project_environmental_commitment_id: uuid(),
                    project_id: owner.projectId,
                    source_framework_id: owner.frameworkId,
                    source_framework_target_id: target.id,
                    sub_category: subCategory,
                    target_type: target.targetType
                });
            });
        });
    });
    return Array.from(recordsByKey.values());
};
/**
 * The category leaves a framework's environmental requirements need to be
 * reportable — including the ones no target is generated from.
 *
 * A target measures what its scope contains, so a target over a category the
 * project does not report is a target nobody can answer. Where the policy names
 * a category, that category is switched on with it — narrowed to a single leaf
 * when the policy names a sub-category too.
 *
 * A target scoped to EVERY category of its type brings every category of that
 * type. That is a lot of leaves — Materials is 182 across 18 categories — and
 * most projects will not use them all, but switching a category off in the
 * category tree is a click, while finding the one the policy needed and
 * switching it on is a hunt.
 */
export const createEnvironmentalCommitmentsFromFramework = (framework) => {
    const definition = findFramework(framework.frameworkId, framework.country, framework.state);
    if (!definition) {
        return [];
    }
    return environmentalCommitmentsForScopes(definition.targets.filter((target) => target.targetCategory === FrameworkTargetCategory.Environmental), { frameworkId: framework.frameworkId, projectId: framework.projectId });
};
/**
 * The environmental targets one framework asks a project to hold.
 *
 * A target, not a category leaf. The two are different things and only this one
 * is a commitment to a figure: a leaf says "we report electricity", a target
 * says "report the electricity we use". The leaves come from
 * {@link createEnvironmentalCommitmentsFromFramework}, which runs beside this.
 *
 * `metricType` is left unset because it is derived from the scope's leaves, and
 * the scope resolves against whatever the project has enabled at the time.
 */
export const createEnvironmentalTargetsFromFramework = (framework) => {
    const definition = findFramework(framework.frameworkId, framework.country, framework.state);
    if (!definition) {
        return [];
    }
    return definition.targets
        .filter((target) => target.targetCategory === FrameworkTargetCategory.Environmental &&
        isGeneratableFrameworkTarget(target))
        .map((target) => ({
        ...(target.category ? { category: target.category } : {}),
        created: new Date(),
        description: target.targetName,
        generationSource: ProjectCommmitmentGenerationSource.Framework,
        id: uuid(),
        ...(target.emissionScopes
            ? { emissionScopes: target.emissionScopes }
            : {}),
        measure: target.measure ?? EnvironmentalTargetMeasure.NativeQuantity,
        measuredBy: deriveEnvironmentalTargetMeasuredBy(target),
        projectId: framework.projectId,
        sourceFrameworkId: framework.frameworkId,
        sourceFrameworkTargetId: target.id,
        ...(target.subCategory ? { subCategory: target.subCategory } : {}),
        targetMethod: target.targetMethod,
        targetType: target.targetType,
        targetValue: target.targetValue ?? 0,
        ...(target.wasteDisposalDestinations
            ? { wasteDisposalDestinations: [...target.wasteDisposalDestinations] }
            : {}),
        ...(target.wasteHazardous !== undefined
            ? { wasteHazardous: target.wasteHazardous }
            : {}),
        ...(target.ecoLabels ? { ecoLabels: [...target.ecoLabels] } : {})
    }));
};
/**
 * The employee register a credit's coverage source counts from, generated when
 * the credit is pursued rather than picked (#113). Only what the credit knows
 * is set: the title names the credit it serves, and frequency, level and the
 * Yes / No question are left to the user, which `requiresSetup` holds them to.
 * `frequency` and `isProjectLevelPolicy` carry placeholders the drawer does not
 * show while the flag is set.
 */
export const createCoverageRegister = (framework, credit, source) => ({
    created: new Date(),
    frequency: PolicyFrequency.OneTime,
    generationSource: ProjectCommmitmentGenerationSource.Framework,
    id: uuid(),
    isForm: false,
    isProjectLevelPolicy: false,
    policyCategory: PolicyCategory.Register,
    policyDescription: `${source.requirementName} (Credit ${credit.creditCode})`,
    policyType: PolicyType.DefaultPolicy,
    projectId: framework.projectId,
    questions: [],
    registerSubject: RegisterSubject.Employee,
    requiresSetup: true,
    sourceFrameworkId: framework.frameworkId
});
export const createDocumentTargetsFromFramework = (framework) => {
    const definition = findFramework(framework.frameworkId, framework.country, framework.state);
    if (!definition) {
        return [];
    }
    return definition.targets
        .filter((target) => target.targetCategory === FrameworkTargetCategory.Documents)
        .map((target) => ({
        created: new Date(),
        frequency: target.frequency,
        generationSource: ProjectCommmitmentGenerationSource.Framework,
        id: uuid(),
        // A framework ships documents, not forms — questions are added by hand.
        isForm: false,
        isProjectLevelPolicy: target.isProjectLevelPolicy,
        // A framework ships blank templates, not questionnaires.
        policyCategory: PolicyCategory.Document,
        policyDescription: target.targetName,
        policyType: target.policyType ?? PolicyType.DefaultPolicy,
        projectId: framework.projectId,
        questions: [],
        sourceFrameworkId: framework.frameworkId,
        sourceFrameworkTargetId: target.id
    }));
};
export const searchFramework = (query) => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
        return [];
    }
    return frameworksList.filter((framework) => {
        const categoryText = framework.targets
            .map((target) => frameworkTargetCategoryToString(target.targetCategory))
            .join(" ");
        const targetNames = framework.targets
            .map((target) => target.targetName)
            .join(" ");
        const searchableValue = [
            framework.frameworkId,
            framework.frameworkName,
            framework.country,
            framework.state,
            categoryText,
            targetNames
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
        return searchableValue.includes(normalizedQuery);
    });
};
